<template>
  <div>
    <!-- Toast component and its position -->
    <Toast position="top-right" />
    
    <!-- Navigation Component -->
    <navigation />
    
    <!-- Toolbar component used to display all the helping action items -->
    <Toolbar class="tool-bar-styling p-mb-4">
      <template slot="left">
        <Button label="New" icon="pi pi-plus" class="p-button-info p-mr-2" v-tooltip="'Click to add new record'" @click="openNewDialog"/>
        <Button label="Bulk Edit" icon="pi pi-pencil" v-tooltip="'Click to edit selected records'" class="p-button-info p-mr-2" :disabled="!selectedProducts || !(selectedProducts.length > 1)" @click="openBulkDialog" />
        <Button label="Delete" icon="pi pi-pencil" v-tooltip="'Click to edit selected records'" class="p-button-info p-mr-2" :disabled="!selectedProducts || !selectedProducts.length" @click="openDeleteConfirm" />
      </template>
      <template slot="right">
        <FileUpload v-tooltip="'Click to import records'" class="p-mr-2"  mode="basic" ref="file" name="file" :customUpload="true"  accept="csv" :maxFileSize="1000000" @uploader="uploadFile" :auto="true" chooseLabel="Import" />
        <Button label="Export" icon="pi pi-upload" v-tooltip="'Click to export liabilities'" class="p-button-help" @click="exportCSV($event)"  />
      </template>
    </Toolbar>

    <div class="data-table-parent-container">
      <DataTable
        dataKey="id" 
        ref="data"
        :paginator="true"
        :selection.sync="selectedProducts"
        scrollDirection="horizontal"
        :scrollable="true"
        :rows="10"
        :value="isLoaded ? planningAllocationData : skeletonData"
        :filters="filters"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" 
        :rowsPerPageOptions="[10,20,30]"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
        style="width: 100%">

        <template #header>
          <div>
            <div class="p-grid p-ai-center vertical-container">
              <div class="p-col-2">Planning Allocation<br>
                <span class="records-count">Total {{planningAllocationCount}} Records</span>
              </div>
              <div class="p-offset-8 p-col-2">
                <span class="p-input-icon-left">
                  <i class="pi pi-search" />
                  <InputText placeholder="Global Search" v-model="filters['global']"/>
                </span>
              </div>
            </div>
          </div>
        </template>
        
        <Column :exportable="false" headerStyle="width: 70px">
          <template #body="slotProps">
            <Button icon="pi pi-fw pi-pencil" class="p-button-rounded p-button-success p-mr-2" @click="openEditDialog(slotProps.data)" />
          </template>
        </Column>

        <Column selectionMode="multiple" :exportable="false" headerStyle="width: 70px"></Column>
        
        <Column v-for="(column,i) in columns" :field="column.field" :header="column.header" :sortable="column.sortable" :headerStyle="`width:${column.width}`" :key="i">
          <template v-if="column.filter" #filter>
            <InputText type="text" v-model="filters[column.field]" class="p-column-filter" :placeholder="`Search by ${column.header}`"/>
          </template>
          <template #body v-if="!isLoaded">
            <Skeleton></Skeleton>
          </template>
        </Column>
      </DataTable>
    </div>
  
    <PlanningAllocationForm 
      v-if="showDialog"
      :closeDialog="closeDialog"
      :handleSubmit="handleSubmit"
      :planningAllocationRecord="planningAllocationRecord" 
      :dialogType="dialogType"/>

    <DeleteConfirm
      v-if="showDeleteConfirm"
      :selectedProductIds="selectedProductIds"
      :closeDeleteConfirm="closeDeleteConfirm"
      :confirmDelete="deleteRecords"/>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Toolbar from "primevue/toolbar";
import FileUpload from "primevue/fileupload";
import Button from "primevue/button";
import http from "../../http-common";
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import Tooltip from 'primevue/tooltip';
import navigation from "../../components/helperComponents/menu";
import Skeleton from 'primevue/skeleton';

import PlanningAllocationForm from './planning-allocation-form'
import DeleteConfirm from './delete-confirm'

export default {
  name: "PlanningAllocationComponent",
  components: {
    navigation,
    Textarea,
    Button,
    FileUpload,
    Toolbar,
    DataTable,
    Column,
    InputText,
    Toast,
    Skeleton,
    PlanningAllocationForm,
    DeleteConfirm
  },
  directives: {
    'tooltip': Tooltip
  },
  data() {
    return {
      file: '',
      planningAllocationData: [],
      showDialog: false,
      showDeleteConfirm: false,
      planningAllocationRecord: {},
      selectedProducts: [],
      filters: {},
      submitted: false,
      enableSaveClonedCTA: false,
      apiUrlPath: 'api/planning-allocation',
      columns: [
        {field: 'id', header: 'ID', width: '100px', sortable: true},
        {field: 'division', header: 'Division', width: '100px', sortable: true, filter: 'text'},
        {field: 'department', header: 'Department', width: '250px', sortable: true, filter: 'text'},
        {field: 'category', header: 'Category', width: '250px', sortable: true, filter: 'text'},
        {field: 'style', header: 'Style', width: '250px', sortable: true, filter: 'text'},
        {field: 'style_name', header: 'Style Name', width: '250px', sortable: true, filter: 'text'},
        {field: 'color', header: 'Color', width: '250px', sortable: true, filter: 'text'},
        {field: 'size', header: 'Size', width: '100px', sortable: true, filter: 'text'},
        {field: 'sku', header: 'SKU', width: '250px', sortable: true, filter: 'text'},
        {field: 'upc', header: 'UPC', width: '250px', sortable: true, filter: 'text'},
        {field: 'qty', header: 'Quantity', width: '250px', sortable: true, filter: false},
        {field: 'from_location', header: 'From Location', width: '250px', sortable: true, filter: 'text'},
        {field: 'from_location_internal_id', header: 'From Location ID', width: '250px', sortable: true, filter: 'text'},
        {field: 'to_location', header: 'To Location', width: '250px', sortable: true, filter: 'text'},
        {field: 'to_location_internal_id', header: 'To Location ID', width: '250px', sortable: true, filter: 'text'},
        {field: 'po_season', header: 'PO Season', width: '250px', sortable: true, filter: 'text'},
        {field: 'qty_allocated', header: 'Quantity allocated', width: '100px', sortable: true, filter: false},
        {field: 'qty_remaining', header: 'Quantity Remaining', width: '100px', sortable: true, filter: false},
        {field: 'inventory_transfer', header: 'Inventory Transfer', width: '250px', sortable: true, filter: 'text'},
        {field: 'created_at', header: 'Created', width: '250px', sortable: true, filter: false},
        {field: 'updated_at', header: 'Updated', width: '250px', sortable: true, filter: false},
      ],
      skeletonData: Array(10)
    }
  },
  computed: {
    isLoaded(){
      return this.planningAllocationData.length > 0
    },
    planningAllocationCount(){
      return this.planningAllocationData.length
    },
    selectedProductIds(){
      return this.selectedProducts.map(el => el.id)
    }
  },
  mounted() {
    this.getAllPlanningAllocation();
  },
  methods: {
    getAllPlanningAllocation(){
      http
      .get(`${this.apiUrlPath}/planning-allocation-entries`)
      .then((response) => {
        this.planningAllocationData = response.data; // JSON are parsed automatically.
      })
      .catch((e) => {
        console.log(e);
      });
    },
    exportCSV() {
      this.$refs.data.exportCSV();
    },
    openNewDialog(){
      this.planningAllocationRecord = {};
      this.dialogType = 'new'
      this.showDialog = true;
    },
    createNewRecord(){
      let body = this.planningAllocationRecord;
      http
      .post(`${this.apiUrlPath}/create-new-planning-allocation-record`, body)
      .then(() => {
        this.$toast.add({severity:'success', summary: 'Successful', detail: 'Created New Planning Allocation Record', life: 3000});
        this.getAllPlanningAllocation();
        this.showDialog = false;
      })
      .catch((e) => {
        this.$toast.add({severity:'error', summary: 'Error', detail: "Couldn't Create New Planning Allocation Record", life: 3000});
        // console.log(e);
      });
    },
    openEditDialog(data){
      this.planningAllocationRecord = {...data};
      this.dialogType = 'edit'
      this.showDialog = true;
    },
    updateRecord(){
      let body = this.planningAllocationRecord;
      http
      .put(`${this.apiUrlPath}/update-record`, body)
      .then(() => {
        this.$toast.add({severity:'success', summary: 'Successful', detail: `Updated Planning Allocation Record: ${body.id}`, life: 3000});
        this.getAllPlanningAllocation();
        this.showDialog = false;
      })
      .catch((e) => {
        this.$toast.add({severity:'error', summary: 'Error', detail: `Couldn't Update Planning Allocation Record: ${body.id}`, life: 3000});
        // console.log(e);
      });
    },
    openBulkDialog(){
      this.planningAllocationRecord = {};
      this.dialogType = 'bulk-edit'
      this.showDialog = true;
    },
    bulkUpdateRecord(){
      let body = {ids: this.selectedProductIds, params: this.planningAllocationRecord}
      if (JSON.parse(JSON.stringify(this.planningAllocationRecord)).upc == null) delete this.planningAllocationRecord.upc 
      http
        .put(`${this.apiUrlPath}/bulk-update-records`, body)
        .then(() => {
          this.$toast.add({
            severity:'success', 
            summary: ( Object.keys(JSON.parse(JSON.stringify(this.planningAllocationRecord))).length ==0 ? '' : 'Successful'), 
            detail: ( Object.keys(JSON.parse(JSON.stringify(this.planningAllocationRecord))).length ==0 ? 'Nothing Updated' : `Updated Planning Allocation Records: ${this.selectedProductIds.join(' ,')}`), 
            life: 3000
          });

          this.getAllPlanningAllocation();
          this.showDialog = false;
          this.selectedProducts = []
        })
        .catch((e) => {
          this.$toast.add({severity:'error', summary: 'Error', detail: `Couldn't Update Planning Allocation Record: ${this.selectedProductIds.join(' ,')}`, life: 3000});
          this.selectedProducts = []
          // console.log(e);
        });
    },
    closeDialog(){
      this.showDialog = false;
    },
    openDeleteConfirm(){
      this.showDeleteConfirm = true;
    },
    closeDeleteConfirm(){
      this.showDeleteConfirm = false
    },
    deleteRecords(){
      let body = {ids: this.selectedProductIds}
      http
        .post(`${this.apiUrlPath}/delete-records`, body)
        .then(() => {
          this.$toast.add({severity:'success', summary: 'Successful', detail: `Deleted Planning Allocation Records: ${this.selectedProductIds.join(' ,')}`, life: 3000});
          this.getAllPlanningAllocation();
          this.showDeleteConfirm = false;
          this.selectedProducts = []
        })
        .catch((e) => {
          this.$toast.add({severity:'error', summary: 'Error', detail: `Couldn't Delete Planning Allocation Records: ${this.selectedProductIds.join(' ,')}`, life: 3000});
          this.selectedProducts = []
          // console.log(e);
        });
    },
    handleSubmit(){
      switch(this.dialogType){
        case 'new':
          return this.createNewRecord();
        case 'edit':
          return this.updateRecord();
        case 'bulk-edit':
          return this.bulkUpdateRecord();
      }
    },
    uploadFile(){
      this.file = this.$refs.file.files[0];
      let formData = new FormData();
      formData.append('file', this.file);
      http
      .post(`${this.apiUrlPath}/uploadfile`,formData,
        { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(() => {
          this.$toast.add({severity:'success', summary: 'Success', detail: "Success", life: 10000});
          this.getAllPlanningAllocation()
          this.$refs.file.files.pop();
        })
        .catch(err => {
          this.$refs.file.files.pop();
          this.getAllPlanningAllocation()
          this.$toast.add({severity:'error', summary: 'Error', detail: err, life: 3000});
      });
    }
  }    
};
</script>

<style>
.custom-dialog .p-inputtext {
  padding: 14px 10px;
  font-size: 16px;
}

.liability-chart.p-inputtext{
  width: 100%;
}

.liability-chart .p-inputtext {
  font-size: 14px;
}
.comment-content {
  color: #00424a;
  font-weight: 700;
}
.data-table-parent-container  {
  overflow: hidden;
}

.data-table-parent-container .p-datatable-scrollable-header {
  overflow: unset;
}

.liability-chart.p-datatable .multi-option {
  color: #4d4d4d !important;
  width: 100%;
}
.liability-chart  .p-datatable-tbody > tr.cloned-liabilities-row {
  background: #4d4d4d;
}

.liability-chart .p-datatable-tbody > tr.cloned-liabilities-row > td {
  color: #fff;
}

.records-count {
  color: #4d4d4d;
  font-size: 12px;
  margin-top: 4px;
}

.data-table-parent-container .p-paginator-bottom {
  position: fixed;
  bottom: 0;
  width: 100%;
}

.data-table-parent-container .p-datatable table {
  margin-bottom: 0;
}

.data-table-parent-container .p-datatable .p-datatable-scrollable-footer {
  background: unset;
}

.tool-bar-styling.p-toolbar {
  background: #f5f5f5;
  border: 1px solid #00424a;
  padding: 1rem;
  margin: 1rem 0;
}

.tool-bar-styling .p-button-info.p-button, .tool-bar-styling .p-button, .tool-bar-styling .p-button.p-button-help {
  background: #00424a;
  border: 1px solid #00424a;
  color: #fff;
}

.tool-bar-styling .p-button-info.p-button:enabled:hover, .tool-bar-styling .p-button.p-fileupload-choose:hover, .tool-bar-styling .p-button.p-button-help:enabled:hover {
  background: #00424a;
  border: 1px solid #00424a;
  color: #fff;
}

.tool-bar-styling .p-button-info.p-button:disabled{
  background: #fff;
  border: 1px solid #333;
  color: #000;
  cursor: not-allowed;
}
</style>